import React, { forwardRef, useId, useState, useEffect, useRef } from 'react';

function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}

const VARIANTS = {
  outline:
    'bg-white border border-gray-300 focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20',
  filled:
    'bg-gray-50 border border-gray-300 focus:bg-white focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20',
  unstyled:
    'bg-transparent border-transparent focus:border-transparent focus:ring-0',
};

const SIZE = {
  sm: 'h-9 text-sm',
  md: 'h-10 text-sm',
  lg: 'h-12 text-base',
};

const PADDING_X = {
  sm: 'px-2.5',
  md: 'px-3',
  lg: 'px-4',
};

const BASE =
  'block w-full rounded-md transition-colors placeholder:text-gray-400 focus:outline-none';
const DISABLED = 'disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed';

const Input = forwardRef(function Input(
  {
    label,
    helperText,
    error,
    required = false,
    as, 
    type = 'text',
    id: idProp,
    name,
    value,
    defaultValue,
    onChange,
    onBlur,
    onFocus,
    placeholder,
    autoComplete,
    autoFocus,
    min,
    max,
    step,
    maxLength,
    rows = 3,
    variant = 'outline', 
    size = 'md', 
    fullWidth = true,
    className,      
    inputClassName,      
    containerClassName,  
    style,
    disabled = false,
    readOnly = false,
    loading = false,
    clearable = false,
    onClear, 
    leftIcon,
    rightIcon,
    prefix,
    suffix,
    ...rest
  },
  ref
) {
  const reactId = useId();
  const id = idProp || `${name || 'input'}-${reactId}`;
  const isTextarea = as === 'textarea' || type === 'textarea';
  const Comp = isTextarea ? 'textarea' : 'input';

  const describedByIds = [];
  if (helperText) describedByIds.push(`${id}-help`);
  if (error) describedByIds.push(`${id}-error`);

  const withLeft = leftIcon || prefix;
  const withRight = rightIcon || suffix || loading || clearable;

  const sizeClasses = `${SIZE[size]} ${PADDING_X[size]}`;
  const variantClasses = VARIANTS[variant];
  const paddingLeft = withLeft ? 'pl-10' : '';
  const paddingRight = withRight ? 'pr-10' : '';
  const errorClasses = error
    ? 'border-red-500 focus:border-red-600 focus:ring-red-600/20'
    : '';

  const inputClasses = cn(
    BASE,
    sizeClasses,
    variantClasses,
    DISABLED,
    paddingLeft,
    paddingRight,
    errorClasses,
    inputClassName
  );

  // Password visibility toggle
  const [showPassword, setShowPassword] = useState(false);
  const effectiveType =
    type === 'password' && showPassword ? 'text' : type;

  // Semi-controlled: maintain internal value so parent re-renders don't reset cursor position
  const isControlled = value !== undefined;
  const [localValue, setLocalValue] = useState(isControlled ? value : '');
  const lastReportedRef = useRef(isControlled ? value : '');

  useEffect(() => {
    if (isControlled && value !== lastReportedRef.current) {
      setLocalValue(value ?? '');
      lastReportedRef.current = value ?? '';
    }
  }, [isControlled, value]);

  const handleChange = (e) => {
    const newVal = e.target.value;
    if (isControlled) {
      setLocalValue(newVal);
      lastReportedRef.current = newVal;
    }
    onChange?.(e);
  };

  const handleClear = (e) => {
    e.preventDefault();
    if (onClear) {
      onClear();
    } else if (onChange) {
      onChange({ target: { value: '' } });
    }
  };

  return (
    <div
      className={cn(fullWidth ? 'w-full' : '', containerClassName, className)}
      style={style}
    >
      {label && (
        <label
          htmlFor={id}
          className="mb-1.5 block text-sm font-medium text-gray-700"
        >
          {label}
          {required && <span className="ml-0.5 text-red-600">*</span>}
        </label>
      )}

      <div className="relative flex items-stretch">
        {leftIcon && (
          <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
            {leftIcon}
          </span>
        )}
        {prefix && (
          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
            {prefix}
          </span>
        )}

        <Comp
          id={id}
          name={name}
          ref={ref}
          type={isTextarea ? undefined : effectiveType}
          rows={isTextarea ? rows : undefined}
          className={inputClasses}
          placeholder={placeholder}
          value={isControlled ? localValue : undefined}
          defaultValue={isControlled ? undefined : defaultValue}
          onChange={isControlled ? handleChange : onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          aria-invalid={!!error || undefined}
          aria-describedby={describedByIds.length ? describedByIds.join(' ') : undefined}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          min={min}
          max={max}
          step={step}
          maxLength={maxLength}
          {...rest}
        />

        {loading && (
          <p>
            Loading...
          </p>
        )}

        {!loading && type === 'password' && (
          <button
            type="button"
            onClick={() => setShowPassword((s) => !s)}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            tabIndex={-1}
          >
            {showPassword ? (
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M3 3l18 18" strokeWidth="2" />
                <path d="M10.58 10.58A2 2 0 0012 14a2 2 0 001.42-.58" strokeWidth="2" />
                <path d="M4.12 4.12C2.28 5.46 1 7.53 1 10c2.5 3.5 6 6 11 6a13.4 13.4 0 006.88-1.88" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : (
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" strokeWidth="2" />
                <circle cx="12" cy="12" r="3" strokeWidth="2" />
              </svg>
            )}
          </button>
        )}

        {!loading && clearable && (value?.length || value === 0) && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
            aria-label="Clear input"
            tabIndex={-1}
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" stroke="currentColor" fill="none">
              <path d="M6 6l12 12M18 6L6 18" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        )}

        {!loading && !clearable && rightIcon && (
          <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400">
            {rightIcon}
          </span>
        )}

        {!loading && !clearable && !rightIcon && suffix && (
          <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">
            {suffix}
          </span>
        )}
      </div>

      {helperText && !error && (
        <p id={`${id}-help`} className="mt-1 text-xs text-gray-500">
          {helperText}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} className="mt-1 text-xs text-red-600">
          {typeof error === 'string' ? error : 'Invalid value'}
        </p>
      )}
    </div>
  );
});

export default Input;